/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { put, select, race, take, all, takeLatest, call } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  ActionRequest,
  ActionSuccess,
  ActionFail,
  XError,
  User,
  Role,
  Ducks,
  Device,
  LocalStorage,
  Credentials,
  StorageKey,
} from '@wit/vtv-x-foundation-lib';
import { StoreState } from 'store/store';
import { Selectors as GdprSelectors } from 'core/store/ducks/gdpr';
import * as ParentalControl from 'core/store/ducks/parentalControl';
import * as AccountData from 'store/ducks/startUp/accountData';
import { callWithProgress } from 'store/ducks/layoutProgressBar';
import { AnyAction } from 'redux';

// ACTIONS
export enum ActionType {
  INIT = 'startUp/INIT',
  SUCCESS = 'startUp/SUCCESS',
  UPDATE_STATUS = 'startUp/UPDATE_STATUS',
  FAIL = 'startUp/FAIL',
  GDPR_FLOW_DONE = 'startUp/gdpr/DONE',
}

type Init = ActionRequest<
  ActionType.INIT,
  { fromLocation?: string; forceUserSwitch?: boolean; forceConsentCheck?: boolean }
>;
type Success = ActionSuccess<ActionType.SUCCESS>;
type UpdateStatus = ActionSuccess<ActionType.UPDATE_STATUS, Status>;
type Fail = ActionFail<ActionType.FAIL>;
type GdprFlowDone = ActionSuccess<ActionType.GDPR_FLOW_DONE>;

type Actions = Init | Success | Fail | UpdateStatus | GdprFlowDone;

const Actions = {
  init: (fromLocation?: string, forceUserSwitch?: boolean, forceConsentCheck?: boolean): Init => ({
    type: ActionType.INIT,
    meta: { fromLocation, forceUserSwitch, forceConsentCheck },
  }),
  sucess: (): Success => ({ type: ActionType.SUCCESS }),
  updateStatus: (status: Status): UpdateStatus => ({ type: ActionType.UPDATE_STATUS, payload: status }),
  fail: (error: XError): Fail => ({
    type: ActionType.FAIL,
    error,
  }),
  gdprFlowDone: (): GdprFlowDone => ({ type: ActionType.GDPR_FLOW_DONE }),
};

export const ActionCreators = {
  init: Actions.init,
  gdprFlowDone: Actions.gdprFlowDone,
};

//
// REDUCER
//

// startup status flow
export enum Status {
  LOADING_CONFIGS = 'LOADING_CONFIGS',
  INITIALIZING_CREDENTIALS = 'INITIALIZING_CREDENTIALS',
  WAITING_UI_AUTHENTICATION = 'WAITING_UI_AUTHENTICATION',
  PREPARING_ACCOUNT = 'PREPARING_ACCOUNT',
  WAITING_UI_GDPR_FLOW = 'WAITING_UI_GDPR_FLOW',
  WAITING_UI_USER_SWITCHING = 'WAITING_UI_USER_SWITCHING',
  LOADING_ACCOUNT_DATA = 'LOADING_ACCOUNT_DATA',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

// normal status flow: undefined > LOADING_CONFIGS > INITIALIZING_CREDENTIALS > WAITING_UI_AUTHENTICATION  > PREPARING_ACCOUNT > WAITING_UI_USER_SWITCHING > LOADING_ACCOUNT_DATA > SUCCESS

type ReducerState = {
  fromLocation?: string;
  status?: Status;
  error?: XError;
};

const initialState: ReducerState = {};

export default function reducer(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionType.INIT:
      return {
        ...state,
        fromLocation: action.meta.fromLocation,
        status: Status.LOADING_CONFIGS,
        error: undefined, // reset error
      };
    case ActionType.UPDATE_STATUS:
      return {
        ...state,
        status: action.payload,
      };
    case ActionType.SUCCESS:
      return {
        ...state,
        status: Status.SUCCESS,
      };
    case ActionType.FAIL:
      return {
        ...state,
        status: Status.ERROR,
        error: action.error,
      };

    default:
      return state;
  }
}

//
// SAGAS
//

// status related with the authentication process
enum AuthenticationStatus {
  NOT_AUTENTICATED = 'NOT_AUTENTICATED',
  AUTHENTICATED_AS_MASTER = 'AUTHENTICATED_AS_MASTER',
  AUTHENTICATED = 'AUTHENTICATED',
}

// private sagas result types
type Result<T = undefined> = { error?: XError; success?: T };
type CredentialsResult = Result<Credentials>;
type AuthenticationResult = Result<AuthenticationStatus>;

type ErrorResult = Result;
const NO_ERROR = { error: undefined };

// private sagas

function* initConfigs(): SagaIterator<ErrorResult> {
  yield put(Ducks.Configs.ActionCreators.loadConfigs());

  const { fail } = yield race({
    success: take(Ducks.Configs.ActionTypes.SUCCESS),
    fail: take(Ducks.Configs.ActionTypes.FAIL),
  });

  if (fail) {
    return { error: fail.error };
  }

  return NO_ERROR;
}

function* initCredentials(): SagaIterator<CredentialsResult> {
  yield put(Ducks.Credentials.Creators.init());

  const { success, fail } = yield race({
    success: take(Ducks.Credentials.ActionTypes.INIT_SUCCESS),
    fail: take(Ducks.Credentials.ActionTypes.INIT_FAIL),
  });

  if (success) {
    return { success: success.payload };
  }

  return { error: fail.error };
}

function* selectUser(userId: string): SagaIterator<ErrorResult> {
  yield put(Ducks.Credentials.Creators.switchUser(userId, true));
  const { error: switchUserFail } = yield race({
    success: take(Ducks.Credentials.ActionTypes.SWITCH_USER_SUCCESS),
    error: take(Ducks.Credentials.ActionTypes.SWITCH_USER_FAIL),
  });

  if (switchUserFail) {
    return { error: switchUserFail.error };
  }

  return NO_ERROR;
}

function* loadDomainData(): SagaIterator<ErrorResult> {
  yield put(Ducks.Household.Creators.getHousehold());
  const { error: domainDataFail } = yield race({
    success: take(Ducks.Household.ActionType.getHousehold.SUCCESS),
    error: take(Ducks.Household.ActionType.getHousehold.FAIL),
  });

  if (domainDataFail) {
    return { error: domainDataFail.error };
  }

  return NO_ERROR;
}

function* deviceRegisterHandling(): SagaIterator<ErrorResult> {
  const isSecurityRedesign: boolean = yield select(Ducks.Dms.Selectors.isSecurityRedesign);
  if (!isSecurityRedesign) {
    const devices: Device[] = yield select(Ducks.Household.Selectors.data.getDevices);
    const deviceUdid: string = yield select(Ducks.DeviceUdid.Selectors.getUdid);

    // checks if it's need to add the device to domain
    const foundDevice: Device | undefined = devices.find((device) => device.udid === deviceUdid);

    // device already in domain
    if (!foundDevice) {
      yield put(Ducks.Household.Creators.addDeviceToDomain(deviceUdid));
      const { error: addDeviceFail } = yield race({
        success: take(Ducks.Household.ActionType.addDeviceToDomain.SUCCESS),
        error: take(Ducks.Household.ActionType.addDeviceToDomain.FAIL),
      });

      if (addDeviceFail) {
        return { error: addDeviceFail.error };
      }
    }
  }

  return NO_ERROR;
}

function* selectedUserHandling(): SagaIterator<AuthenticationResult> {
  const users: User[] = yield select(Ducks.Household.Selectors.data.getUsers);

  const credentialsUserId: string = yield select(Ducks.Credentials.Selectors.getUserId);
  const autenticatedUser = users.find((user) => user.id === credentialsUserId);

  const preferredUserId: string | null = yield call(LocalStorage.getPreferredUserId);
  const preferredUser = users.find((user) => user.id === preferredUserId);

  if (preferredUser && autenticatedUser && preferredUser !== autenticatedUser) {
    const selectUserRes: ErrorResult = yield call(selectUser, preferredUser.id);

    if (!selectUserRes.error) {
      return { success: AuthenticationStatus.AUTHENTICATED };
    }
  }

  if (autenticatedUser?.role !== Role.MASTER) {
    return { success: AuthenticationStatus.AUTHENTICATED };
  }

  return { success: AuthenticationStatus.AUTHENTICATED_AS_MASTER };
}

function* hopsCallBack(): SagaIterator<boolean> {
  yield call(initCredentials);
  const isAuthenticated: boolean = yield select(Ducks.Credentials.Selectors.isAuthenticated);

  if (!isAuthenticated) {
    return false;
  }

  const domainRes: ErrorResult = yield call(loadDomainData);

  if (domainRes.error) {
    return false;
  }

  return true;
}

function* preparingAccount(): SagaIterator<AuthenticationResult> {
  const domainRes: ErrorResult = yield call(loadDomainData);

  if (domainRes.error) {
    return domainRes;
  }

  // Check if there are DMS hops to run before proceeding
  yield call(Ducks.DmsHops.tryRunDmsHops, hopsCallBack);

  // Some DMS configurations need to be filtered based on customerType
  yield call(Ducks.Dms.filterByCustomerType, yield select(Ducks.Household.Selectors.data.getCustomerType));

  const deviceRegisterRes: ErrorResult = yield call(deviceRegisterHandling);

  if (deviceRegisterRes.error) {
    return deviceRegisterRes;
  }

  return yield call(selectedUserHandling);
}

function* handleHouseholdSwitching() {
  // clean the Age Rating key from LocalStorage and reload again in order to set the default value
  yield call(LocalStorage.removeData, StorageKey.AGE_RATING);
  yield put(ParentalControl.ActionCreators.loadAgeRating());
  yield race([
    take(ParentalControl.ActionTypes.LOAD_AGE_RATING_SUCCESS),
    take(ParentalControl.ActionTypes.LOAD_AGE_RATING_FAIL),
  ]);
}

// handling all stages of the startup flow process
function* initFlowSaga(action: AnyAction): SagaIterator {
  const { forceUserSwitch = false, forceConsentCheck = false } = action.meta || {};

  let isAutenticated = forceUserSwitch && (yield select(Ducks.Credentials.Selectors.isAuthenticated));

  if (action.type === ActionType.INIT && !isAutenticated) {
    const initConfigsRes: ErrorResult = yield callWithProgress(initConfigs);

    if (initConfigsRes.error) {
      yield put(Actions.fail(initConfigsRes.error));
      return;
    }

    yield put(Actions.updateStatus(Status.INITIALIZING_CREDENTIALS));
    yield callWithProgress(initCredentials);

    // in this step sec. red. errors may be ignored. what is important is if user is authenticated
    isAutenticated = yield select(Ducks.Credentials.Selectors.isAuthenticated);
  }

  if (!isAutenticated) {
    const prevUser: Credentials | undefined = yield call(LocalStorage.getCredentials);
    // not autenticated could be caused by sec. red. errors or because user never been authenticated

    // informs UI to request credentials from user
    yield put(Actions.updateStatus(Status.WAITING_UI_AUTHENTICATION));

    // waits for a success login
    yield take([Ducks.Credentials.ActionTypes.SUCCESS, Ducks.Credentials.ActionTypes.LOGIN_CODE_SUCCESS]);

    // Compare the previous user logged in with the current logged in user.
    // If they are the same, Age Rating on localStorage will be persisted, otherwise, cleaned
    const currentUser: Credentials | undefined = yield call(LocalStorage.getCredentials);

    if (prevUser !== undefined && prevUser.householdId !== currentUser?.householdId) {
      yield call(handleHouseholdSwitching);
    }
  }

  yield put(Actions.updateStatus(Status.PREPARING_ACCOUNT));
  const preparingAccountRes: AuthenticationResult = yield callWithProgress(preparingAccount);

  if (preparingAccountRes.success) {
    // check GDPR
    const gdprDocuments = yield select(GdprSelectors.getDocumentsToShow);
    if ((gdprDocuments.notice || gdprDocuments.consent) && (!forceUserSwitch || forceConsentCheck)) {
      yield put(Actions.updateStatus(Status.WAITING_UI_GDPR_FLOW));
      yield take(ActionType.GDPR_FLOW_DONE);
    }

    if (preparingAccountRes.success === AuthenticationStatus.AUTHENTICATED_AS_MASTER || forceUserSwitch) {
      // when authenticated with the master we need to request user to select a different user from list
      yield put(Actions.updateStatus(Status.WAITING_UI_USER_SWITCHING));
      yield take(Ducks.Credentials.ActionTypes.SWITCH_USER_SUCCESS);
    }

    yield put(Actions.updateStatus(Status.LOADING_ACCOUNT_DATA));
    const loadRes: ErrorResult = yield callWithProgress(AccountData.load);
    if (loadRes.error) {
      yield put(Actions.fail(loadRes.error));
      return;
    }

    // flow finished with success
    yield put(Actions.sucess());
  } else {
    yield put(Actions.fail(preparingAccountRes.error || XError.fromError(new Error('Unknown'))));
  }
}

// public sagas
export function* saga(): SagaIterator {
  yield all([takeLatest([ActionType.INIT, Ducks.Credentials.ActionTypes.LOGOUT_SUCCESS], initFlowSaga)]);
}

//
// SELECTORS
//

export const Selectors = {
  status: (state: StoreState): Status | undefined => state.startUp?.status,
  error: (state: StoreState): XError | undefined => state.startUp?.error,
  fromLocation: (state: StoreState): string | undefined => state.startUp?.fromLocation,
  isStartUpSuccess: (state: StoreState): boolean => state.startUp?.status === Status.SUCCESS,
  isAfterInitializingCredentials: (state: StoreState): boolean =>
    state.startUp?.status === Status.WAITING_UI_AUTHENTICATION ||
    state.startUp?.status === Status.PREPARING_ACCOUNT ||
    state.startUp?.status === Status.WAITING_UI_GDPR_FLOW ||
    state.startUp?.status === Status.WAITING_UI_USER_SWITCHING ||
    state.startUp?.status === Status.LOADING_ACCOUNT_DATA ||
    state.startUp?.status === Status.SUCCESS ||
    state.startUp?.status === Status.ERROR,
};
